import { gql, useQuery } from "@apollo/client";
import type { DOMNode, Element, HTMLReactParserOptions } from "html-react-parser";
import parse, { attributesToProps, domToReact } from "html-react-parser";
import { t } from "i18next";
import React from "react";
import { Trans } from "react-i18next";

import type { IShopQuery } from "@repo/shopify/queries/others-gql";
import { shopQuery } from "@repo/shopify/queries/others-gql";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import { NOTIFICATIONS } from "@const/notifications";

import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

export default function ShippingAndReturns() {
  const { lang } = useLanguage();
  const { openToast } = useLayoutStore();

  const { data, loading } = useQuery<IShopQuery>(gql(shopQuery.query), {
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  return (
    <>
      {loading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-10 col-span-8 grid grid-cols-subgrid gap-y-10 wrap-break-word">
          <section className="pc:col-span-4 col-span-8 grid h-fit grid-cols-subgrid flex-col gap-y-5">
            <h2 className="text-title pc:col-span-4 col-span-8">Shipping</h2>
            <div className="pc:col-span-6 col-span-8 grid grid-cols-subgrid gap-y-1.5">
              <span className="pc:col-span-1 col-span-2">{t("information.shipping.method")}</span>
              <span className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">{t("information.shipping.method_description")}</span>
              <span className="pc:col-span-1 col-span-2">{t("information.shipping.zone")}</span>
              <span className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">
                <Trans i18nKey="information.shipping.zone_description" />
              </span>
              <span className="pc:col-span-1 col-span-2">{t("information.shipping.time")}</span>
              {lang === "en" ? (
                <div className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">
                  <span className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">{t("information.shipping.time_description")}</span>
                  <span className="text-dark-grey"> {t("information.shipping.description")}</span>
                </div>
              ) : (
                <>
                  <span className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">{t("information.shipping.time_description")}</span>
                  <span className="pc:col-span-1 col-span-2">{t("information.shipping.rate")}</span>
                  <div className="pc:col-span-3 col-span-6 flex flex-col gap-1.5">
                    <span>{t("information.shipping.rate_description")}</span>
                    <span className="text-dark-grey"> {t("information.shipping.description")}</span>
                  </div>
                </>
              )}
            </div>
          </section>
          <section className="pc:col-span-4 tnt-gap-x col-span-8 grid h-fit grid-cols-subgrid gap-y-5">
            <h2 className="text-title pc:col-span-4 col-span-8">Returns</h2>
            <section className="pc:col-span-4 col-span-8 grid grid-cols-subgrid gap-y-10">
              <ParseContent html={data?.shop.refundPolicy?.body || ""} />
            </section>
          </section>
        </section>
      )}
    </>
  );
}

function ParseContent({ html }: { html: string }) {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      const domParse = domNode as Element;

      if (domParse?.name === "a") {
        const linkText = domToReact(domParse.children as DOMNode[], options) as JSX.Element;
        return (
          <Link as="text" {...attributesToProps(domParse.attribs)} className={domParse.attribs.class} to="">
            {linkText}
          </Link>
        );
      }

      if (domParse?.name === "button") {
        const buttonText = domToReact(domParse.children as DOMNode[], options) as JSX.Element;
        return <Button className={domParse?.attribs.class}>{buttonText}</Button>;
      }

      if (domParse.name === "h1" || domParse.name === "h2" || domParse.name === "h3" || domParse.name === "h4") {
        const headingText = domToReact(domParse.children as DOMNode[], options) as React.JSX.Element;
        const ParseDom = domParse.name;
        return <ParseDom className={domParse.attribs.class}>{headingText}</ParseDom>;
      }
    },
  };

  return <>{parse(html, options)}</>;
}
